/**
 * الدعم الفني — the platform admin's inbox of support conversations.
 *
 * Every message users send from the help panel lands here with who sent it
 * (name, job, hospital), the screen they were on, and the screenshot taken of
 * it. The admin answers in the conversation; the user's launcher lights up.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { EmptyState, Icon } from '../../components/common';
import { HeroBand, StatTile3D } from '../../components/common/Tile3D';
import { supportApi } from '../../api/endpoints';
import { usePoll } from '../../hooks/usePolling';
import { StatusChip, SupportThread } from '../help/SupportCenter';

const FILTERS = [
  ['open', 'بانتظار الرد', 'hourglass'],
  ['answered', 'تم الرد', 'check-circle-2'],
  ['closed', 'مغلقة', 'lock'],
  ['', 'الكل', 'inbox'],
];

const when = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' });
};

export function AdminSupport() {
  const [status, setStatus] = useState('open');
  const [tickets, setTickets] = useState(null);
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState(null);
  const threadRef = useRef(null);

  // Below lg the conversation sits under the whole list: bring it into view
  // when one is opened, or the tap looks like it did nothing.
  useEffect(() => {
    if (!selected || window.matchMedia?.('(min-width: 1024px)').matches) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    threadRef.current?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  }, [selected]);

  const load = useCallback(async () => {
    const [list, everything] = await Promise.all([supportApi.tickets(status ? { status } : {}), supportApi.tickets({})]);
    setTickets(list);
    setAll(everything);
  }, [status]);

  useEffect(() => {
    setTickets(null);
    load().catch(() => setTickets([]));
  }, [load]);
  usePoll(load, 20000, { immediate: false });

  const count = (s) => all.filter((t) => t.status === s).length;

  return (
    <div>
      <HeroBand icon="headset" tone="cyan" title="الدعم الفني" subtitle="رسائل المستخدمين مع لقطات الشاشة — رد عليهم من هنا ويصلهم الرد فوراً في المساعد." />
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile3D icon="hourglass" label="بانتظار الرد" value={count('open')} tone="amber" delay={0} onClick={() => setStatus('open')} />
        <StatTile3D icon="mail-open" label="غير مقروءة" value={all.filter((t) => t.unreadForSupport && t.status !== 'closed').length} tone="rose" delay={1} />
        <StatTile3D icon="check-circle-2" label="تم الرد" value={count('answered')} tone="emerald" delay={2} onClick={() => setStatus('answered')} />
        <StatTile3D icon="lock" label="مغلقة" value={count('closed')} tone="slate" delay={3} onClick={() => setStatus('closed')} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
        <div className="glass-panel min-w-0 rounded-2xl p-3">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {FILTERS.map(([id, label, icon]) => (
              <button key={id || 'all'} type="button" aria-pressed={status === id} onClick={() => setStatus(id)} className={`chip-toggle ${status === id ? 'chip-toggle--on' : ''}`}>
                <Icon name={icon} size={13} /> {label}
              </button>
            ))}
          </div>
          {!tickets ? (
            <p className="p-4 text-center text-xs text-slate-400">جارٍ التحميل…</p>
          ) : tickets.length === 0 ? (
            <EmptyState icon="inbox" title="لا توجد رسائل هنا" />
          ) : (
            <div className="space-y-1.5">
              {tickets.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelected(t.id)}
                  className={`support-row w-full ${selected === t.id ? 'support-row--on' : ''}`}
                >
                  <span className="support-row__icon" aria-hidden="true">
                    <Icon name="user" size={14} />
                  </span>
                  <span className="min-w-0 flex-1 text-start">
                    <span className="block truncate text-sm font-bold text-white">{t.subject}</span>
                    <span className="block truncate text-[10.5px] text-slate-400">
                      {t.userName || '—'} · {t.roleTitle || ''}
                      {t.institutionName ? ` · ${t.institutionName}` : ''}
                    </span>
                    <span className="block truncate text-[10px] text-slate-500">{when(t.lastMessageAt || t.createdAt)}</span>
                  </span>
                  {t.unreadForSupport && t.status !== 'closed' && <span className="support-row__dot" aria-label="جديد" />}
                  <StatusChip status={t.status} />
                </button>
              ))}
            </div>
          )}
        </div>
        {/* An open conversation fills a phone's screen, so it can scroll to the top. */}
        <div
          ref={threadRef}
          className={`glass-panel support-inbox flex min-w-0 scroll-mt-20 flex-col overflow-hidden rounded-2xl lg:min-h-[32rem] ${
            selected ? 'min-h-[calc(100dvh-6rem)]' : 'min-h-[32rem]'
          }`}
        >
          {selected ? (
            <SupportThread key={selected} ticketId={selected} asSupport onChanged={() => load().catch(() => {})} />
          ) : (
            <EmptyState icon="messages-square" title="اختر محادثة" message="تظهر هنا رسائل المستخدم ولقطات الشاشة، ويمكنك الرد أو إغلاق المحادثة." />
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminSupport;
